import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  DAILY_RECURRENCE,
  DAYS_OF_WEEK,
  MONTHLY_RECURRENCE,
  RADIO_AFTER,
  RADIO_NEVER,
  RADIO_ON,
  WEEKLY_RECURRENCE,
  YEARLY_RECURRENCE,
} from "../../../../constants";
import convertTZ from "../../../../helpers/Date/convertTimezone";
import dataFromRrule from "../../../../helpers/Date/dataFromRrule";
import { generateRRule } from "../../../../helpers/generateRrule";
import getOrdinalOfNumber from "../../../../helpers/Utilities/getOrdinalOfNumber";

const DEFAULT_DAY_SELECT_OPTIONS = [
  { dayEbr: "M", active: false },
  { dayEbr: "T", active: false },
  { dayEbr: "W", active: false },
  { dayEbr: "TH", active: false },
  { dayEbr: "F", active: false },
  { dayEbr: "SA", active: false },
  { dayEbr: "SU", active: false },
];

const useCustomRecurrencePopupData = ({ popupData, closePopup }) => {
  // State for tracking selected days for recurrence
  const [daySelectOptions, setDaySelectOptions] = useState([]);
  // State for tracking selected custom month type repetetion
  const [monthlySelectOptions, setMonthlySelectOptions] = useState([]);
  const [activeRadioButton, setActiveRadio] = useState("Never");
  const [repetition, setRepetition] = useState(1);

  const [activeFrequencyOption, setActiveFrequencyOption] = useState({
    label: `week${repetition > 1 ? "s" : ""}`,
    value: `week${repetition > 1 ? "s" : ""}`,
  });

  // set custom monthly repeat options
  useEffect(() => {
    const dateObj = moment.tz(
      popupData?.item?.startDate,
      popupData?.item?.timezone
    );

    const dayOfMonth = dateObj.get("date");
    const day = dateObj.day();
    const days = DAYS_OF_WEEK.map((d) => d.label);
    const displayDay = days[day];

    const nThDayOfWeek = Math.ceil(dayOfMonth / 7);

    const ordinal = getOrdinalOfNumber(nThDayOfWeek);

    const options = [
      {
        label: `Monthly on day ${dayOfMonth}`,
        value: `Monthly on day ${dayOfMonth}`,
        BYMONTHDAY: dayOfMonth,
      },
      {
        label: `Monthly on the ${nThDayOfWeek}${ordinal} ${displayDay}`,
        value: `Monthly on the ${nThDayOfWeek}${ordinal} ${displayDay}`,
        BYDAY: +nThDayOfWeek,
        displayDay,
      },
    ];

    const currentMonth = dateObj.get("month");
    const nextWeekMonth = dateObj.add(1, "week").get("month");

    if (currentMonth !== nextWeekMonth) {
      // if day is in last week
      options.push({
        label: `Monthly on the last ${displayDay}`,
        value: `Monthly on the last ${displayDay}`,
        BYDAY: -1,
        displayDay,
      });
    }
    // default to first/bymonthday
    let selectedOptionIndex = 0;
    // if existing that set that as active
    if (
      popupData?.item?.recurrence?.label === "Custom Recurrence" &&
      popupData?.item?.recurrence?.value
    ) {
      const rruleObject = dataFromRrule(popupData?.item?.recurrence?.value);
      const { bynweekday } = rruleObject;

      if (bynweekday?.length) {
        const integer = bynweekday[0][1];
        const foundIndex = options.findIndex((opt) => opt.BYDAY === integer);
        if (foundIndex !== -1) {
          // if bynweekday, then update index
          selectedOptionIndex = foundIndex;
        }
      }
    }

    options[selectedOptionIndex].active = true;

    setMonthlySelectOptions(options);
  }, [
    popupData?.item?.recurrence?.label,
    popupData?.item?.recurrence?.value,
    popupData?.item?.startDate,
    popupData?.item?.timezone,
  ]);

  useEffect(() => {
    setActiveFrequencyOption((prevOption) => {
      switch (true) {
        case prevOption.value.includes("week"):
          return {
            ...prevOption,
            label: `week${repetition > 1 ? "s" : ""}`,
            value: `week${repetition > 1 ? "s" : ""}`,
          };
        case prevOption.value.includes("day"):
          return {
            ...prevOption,
            label: `day${repetition > 1 ? "s" : ""}`,
            value: `day${repetition > 1 ? "s" : ""}`,
          };
        case prevOption.value.includes("month"):
          return {
            ...prevOption,
            label: `month${repetition > 1 ? "s" : ""}`,
            value: `month${repetition > 1 ? "s" : ""}`,
          };
        case prevOption.value.includes("quarter"):
          return {
            ...prevOption,
            label: `quarter${repetition > 1 ? "s" : ""}`,
            value: `quarter${repetition > 1 ? "s" : ""}`,
          };
        case prevOption.value.includes("year"):
          return {
            ...prevOption,
            label: `year${repetition > 1 ? "s" : ""}`,
            value: `year${repetition > 1 ? "s" : ""}`,
          };
        default:
          return prevOption;
      }
    });
  }, [repetition]);

  const frequencyOptions = useMemo(() => {
    return [
      {
        label: `day${repetition > 1 ? "s" : ""}`,
        value: `day${repetition > 1 ? "s" : ""}`,
      },
      {
        label: `week${repetition > 1 ? "s" : ""}`,
        value: `week${repetition > 1 ? "s" : ""}`,
      },
      {
        label: `month${repetition > 1 ? "s" : ""}`,
        value: `month${repetition > 1 ? "s" : ""}`,
      },
      {
        label: `quarter${repetition > 1 ? "s" : ""}`,
        value: `quarter${repetition > 1 ? "s" : ""}`,
      },
      {
        label: `year${repetition > 1 ? "s" : ""}`,
        value: `year${repetition > 1 ? "s" : ""}`,
      },
    ];
  }, [repetition]);

  //  date should default to a date 3 months in the future
  const todaysDate = new Date();
  const threeMonthsFromNow = new Date(
    todaysDate.setMonth(todaysDate.getMonth() + 3)
  );
  const [date, setDate] = useState(threeMonthsFromNow);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [occurrences, setOccurrences] = useState(14);

  const getDaySelectionOptions = useCallback((byweekday) => {
    const daysOptions = DEFAULT_DAY_SELECT_OPTIONS?.map((option, index) => {
      const activeOption =
        Array.isArray(byweekday) &&
        byweekday?.find((active) => {
          return active === index;
        });
      return activeOption !== undefined ? { ...option, active: true } : option;
    });

    return daysOptions;
  }, []);

  useEffect(() => {
    let origRepetition = "";

    if (popupData?.item?.recurrence) {
      // determine if there are selected days present in the rule
      const haveWeekDaysSelected = popupData?.item?.recurrence
        ?.toLowerCase()
        ?.includes("byday=");

      // NOTE: if there's no selected days, RRule still introduces byweekday: [1] in the options
      const rruleObject = dataFromRrule(popupData?.item?.recurrence);
      const { byweekday, count, frequency, interval, until } = rruleObject;

      // if anyting other than zero, set the repetition
      if (interval) {
        origRepetition = interval;
        setRepetition(origRepetition);
      }

      // if the frequency matches daily pattern set the frequency option
      if (frequency === DAILY_RECURRENCE) {
        setActiveFrequencyOption({
          label: `day${origRepetition > 1 ? "s" : ""}`,
          value: `day${origRepetition > 1 ? "s" : ""}`,
        });
      }
      // if the frequency matches weekly pattern set the frequency option
      if (frequency === WEEKLY_RECURRENCE) {
        setActiveFrequencyOption({
          label: `week${origRepetition > 1 ? "s" : ""}`,
          value: `week${origRepetition > 1 ? "s" : ""}`,
        });

        // set the daySelectOptions
        if (haveWeekDaysSelected) {
          setDaySelectOptions(getDaySelectionOptions(byweekday));
        } else {
          setDaySelectOptions(DEFAULT_DAY_SELECT_OPTIONS);
        }
      }
      // if the frequency matches monthly pattern set the frequency option
      if (frequency === MONTHLY_RECURRENCE) {
        setActiveFrequencyOption({
          label: `month${origRepetition > 1 ? "s" : ""}`,
          value: `month${origRepetition > 1 ? "s" : ""}`,
        });
      }
      // if the frequency matches yearly pattern set the frequency option
      if (frequency === YEARLY_RECURRENCE) {
        setActiveFrequencyOption({
          label: `year${origRepetition > 1 ? "s" : ""}`,
          value: `year${origRepetition > 1 ? "s" : ""}`,
        });
      }
      // if until property is not null set radio to On and set the date
      if (until) {
        setActiveRadio(RADIO_ON);

        const newOffset = moment
          .tz(rruleObject.until, popupData?.item?.timezone)
          .utcOffset();
        const formattedDate = moment(rruleObject.until).utcOffset(
          newOffset,
          true
        );
        // set until date in correct timezone
        setDate(convertTZ(formattedDate, popupData?.item?.timezone));
      }
      // if count property is not null set radio to After and set the occurrences
      if (count) {
        setActiveRadio(RADIO_AFTER);
        setOccurrences(count);
      }
      // if the count and until is null set radio to Never
      if (!count && !until) {
        setActiveRadio(RADIO_NEVER);
      }
    } else {
      // initialize days select options
      setDaySelectOptions(
        DEFAULT_DAY_SELECT_OPTIONS?.map((option, index) => {
          if (index !== 0) return option;
          return { ...option, active: true };
        })
      );
    }
  }, [popupData, getDaySelectionOptions]);
  // Handle the submission of the recurrence data

  const handleSubmit = () => {
    const { rruleString, rruleObject } = generateRRule(
      repetition,
      activeFrequencyOption,
      daySelectOptions,
      activeRadioButton,
      date,
      occurrences,
      monthlySelectOptions
    );

    let ruleToText = rruleObject.toText();
    // update until date to show in correct timezone
    const indexOfUNTIL = ruleToText?.indexOf("until");
    if (indexOfUNTIL !== -1) {
      ruleToText = ruleToText.slice(0, indexOfUNTIL + 6);
      ruleToText += `${moment
        .tz(date, popupData?.item?.timezone)
        .format("MMM DD yyyy")}`;
    }
    const ruleWithCapitalizedFirstLetter =
      ruleToText.charAt(0).toUpperCase() + ruleToText.slice(1);

    const startDateFormatted = moment
      .tz(popupData?.item?.startDate, popupData?.item?.timezone)
      .format("MMM DD yyyy");

    popupData.setRecurrenceString(
      `${ruleWithCapitalizedFirstLetter} starting ${startDateFormatted}`
    );

    popupData.dispatch({
      type: "recurrence",
      value: {
        label: "Custom Recurrence",
        value: rruleString,
      },
    });
    closePopup();
  };

  // Update the number of occurrences to stop the recurrence
  const setNumberToStop = (targetNum) => {
    if (targetNum === "") {
      setOccurrences("");
      return;
    }

    if (Number.isNaN(Number(targetNum))) {
      return;
    }

    if (Number(targetNum <= 0)) {
      setOccurrences("");
      return;
    }

    const formattedNumber = targetNum.replace(/^0+/, "");

    setOccurrences(formattedNumber);
  };

  useEffect(() => {
    // Initializing criteria flags to determine if the save button should be enabled
    let criteria1met = false;
    let criteria2met = false;

    // Check for the first set of criteria:
    // If a radio button is active, a repetition value is set, and a frequency is selected
    if (
      activeRadioButton !== "" &&
      repetition &&
      repetition > 0 &&
      activeFrequencyOption !== null
    ) {
      criteria1met = true;
    }

    // Check for the second set of criteria:
    // If a radio button is active.
    // If After selected, occurrence must be > 0
    criteria2met =
      activeRadioButton === RADIO_AFTER ? occurrences && occurrences > 0 : true;

    const canSave = criteria1met && criteria2met;

    // If both criterium are met, enable the save button, otherwise disable it
    if (canSave) {
      // if (repetition && repetition > 0 && (criteria1met || criteria2met)) {
      setSaveButtonDisabled(false);
    } else {
      setSaveButtonDisabled(true);
    }
  }, [
    daySelectOptions,
    repetition,
    date,
    activeRadioButton,
    occurrences,
    activeFrequencyOption,
  ]);

  // Set the end date for the recurrence
  const setEndDate = (dateISO) => {
    // make sure the date is not in the past
    if (new Date(dateISO) < new Date()) {
      return;
    }
    // set until date in correct timezone
    const newOffset = moment.tz(dateISO, popupData?.item?.timezone).utcOffset();
    const formattedDate = moment(dateISO).utcOffset(newOffset, true);
    setDate(convertTZ(formattedDate, popupData?.item?.timezone));
  };

  // Set the number of times to repeat the recurrence
  const setFreqRepetition = (targetNum) => {
    if (targetNum === "") {
      setRepetition("");
      return;
    }

    if (Number.isNaN(Number(targetNum))) {
      return;
    }

    if (Number(targetNum) <= 0) {
      setRepetition("");
      return;
    }

    const formattedNumber = targetNum.replace(/^0+/, "");

    // remove leading zero from the number
    setRepetition(formattedNumber);
  };

  // Set the selected frequency option
  const setFreqOptionSelected = (targetLabel) => {
    setActiveFrequencyOption({ label: targetLabel, value: targetLabel });
  };

  // Toggle the active state of a day
  const toggleDayActive = (targetDayEbr) => {
    setDaySelectOptions((prev) =>
      prev.map((item) =>
        item.dayEbr === targetDayEbr ? { ...item, active: !item.active } : item
      )
    );
  };

  const isDaySelectShown = useMemo(() => {
    const weekOptionSelected = activeFrequencyOption?.label.includes("week");

    if (!weekOptionSelected) {
      setDaySelectOptions((prev) =>
        prev.map((item) => ({ ...item, active: false }))
      );
    } else if (
      popupData?.item?.recurrence?.label === "Custom Recurrence" &&
      popupData?.item?.recurrence?.value
    ) {
      // NOTE: if there's no selected days, RRule still introduces byweekday: [1] in the options
      const rruleObject = dataFromRrule(popupData?.item?.recurrence?.value);
      const { byweekday } = rruleObject;

      if (byweekday) {
        setDaySelectOptions(getDaySelectionOptions(byweekday));
      } else {
        setDaySelectOptions(DEFAULT_DAY_SELECT_OPTIONS);
      }
    } else {
      setDaySelectOptions(DEFAULT_DAY_SELECT_OPTIONS);
    }
    return weekOptionSelected;
  }, [
    activeFrequencyOption?.label,
    getDaySelectionOptions,
    popupData?.item?.recurrence,
  ]);

  // Set the active monthly repeat option
  const setMonthlyRepeatActiveOption = (value) => {
    setMonthlySelectOptions((prev) =>
      prev.map((item) => ({ ...item, active: item.value === value }))
    );
  };

  const activeMonthylSelectOption = useMemo(() => {
    return monthlySelectOptions.find((option) => !!option.active);
  }, [monthlySelectOptions]);

  const isMonthSelectShown = useMemo(() => {
    const monthOptionSelected = activeFrequencyOption?.label.includes("month");

    return monthOptionSelected;
  }, [activeFrequencyOption]);

  return {
    activeRadioButton,
    daySelectOptions,
    frequencyOptions,
    repetition,
    date,
    saveButtonDisabled,
    occurrences,
    activeFrequencyOption,
    isDaySelectShown,
    isMonthSelectShown,
    handleSubmit,
    setNumberToStop,
    setEndDate,
    setFreqRepetition,
    setFreqOptionSelected,
    setDaySelectOptions,
    setActiveRadio,
    toggleDayActive,
    activeMonthylSelectOption,
    monthlySelectOptions,
    setMonthlyRepeatActiveOption,
  };
};

export default useCustomRecurrencePopupData;
